/** Pause menu overlay — shows controls summary and provides navigation to attunement, debug, and main menu. */
import Phaser from 'phaser';
import { KeyBindings, GameAction } from '../systems/KeyBindings';
import { SaveSystem } from '../systems/SaveSystem';
import { crispText, crispRect, crispButton, setupCrispCamera } from '../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, Color } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';
import { DebugConfig } from '../debug/DebugConfig';

function buildControlsDisplay(): { key: string; action: string }[] {
  const up = KeyBindings.getBinding(GameAction.MoveUp);
  const moveLabel = KeyBindings.keyLabel(up.primary) +
    KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.MoveLeft).primary) +
    KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.MoveDown).primary) +
    KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.MoveRight).primary) +
    (up.alternate !== undefined ? ' / Arrows' : '');

  return [
    { key: moveLabel, action: 'Move' },
    { key: KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.Run).primary) + ' (hold)', action: 'Run' },
    { key: KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.Dash).primary), action: 'Dash' },
    { key: KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.Skill1).primary), action: 'Punch (Slot 1)' },
    { key: '1-5', action: 'Skill Slots' },
    { key: KeyBindings.keyLabel(KeyBindings.getBinding(GameAction.Pause).primary), action: 'Pause' },
    { key: 'F1', action: 'Debug Mode' },
  ];
}

/**
 * Displays a darkened overlay with live control bindings read from KeyBindings, and buttons
 * to resume, open attunement, open debug settings, or return to the main menu.
 */
export class PauseScene extends Phaser.Scene {
  private escKey!: Phaser.Input.Keyboard.Key;

  constructor() {
    super({ key: 'PauseScene' });
  }

  create(): void {
    setupCrispCamera(this);
    const width = GAME_WIDTH;
    const height = GAME_HEIGHT;

    // Semi-transparent overlay
    crispRect(this, width / 2, height / 2, width, height, Color.black, 0.7);

    crispText(this, width / 2, height * 0.12, 'PAUSED', {
      fontFamily: Font.family, fontSize: Font.sizeTitleLg, color: Hex.greenBright,
    }).setOrigin(0.5);

    // Controls section
    const controls = buildControlsDisplay();
    const controlsY = height * 0.25;
    crispText(this, width / 2, controlsY, '- CONTROLS -', {
      fontFamily: Font.family, fontSize: '16px', color: Hex.textMuted,
    }).setOrigin(0.5);

    const lineHeight = 20;
    const keyColX = width / 2 - 80;
    const actionColX = width / 2 + 80;

    for (let i = 0; i < controls.length; i++) {
      const y = controlsY + 26 + i * lineHeight;
      crispText(this, keyColX, y, controls[i].key, TextStyle.bodyKey).setOrigin(1, 0.5);
      crispText(this, actionColX, y, controls[i].action, TextStyle.body).setOrigin(0, 0.5);
    }

    // Separator dots between key and action columns
    for (let i = 0; i < controls.length; i++) {
      const y = controlsY + 26 + i * lineHeight;
      crispText(this, width / 2, y, '···', TextStyle.bodyMuted).setOrigin(0.5);
    }

    // Buttons
    const btnY = controlsY + 26 + controls.length * lineHeight + 30;

    crispButton(this, width / 2, btnY, '[ RESUME ]',
      Font.sizeButton, ButtonColor.primary, () => this.resume());
    crispButton(this, width / 2, btnY + 40, '[ ATTUNEMENTS ]',
      Font.sizeButton, ButtonColor.attune, () => this.openAttunement());

    // Event log toggle — reads/writes GameSettings.eventLogEnabled
    const saveSystem = new SaveSystem();
    let eventLogOn = saveSystem.loadSettings().eventLogEnabled;
    const logBtn = crispButton(this, width / 2, btnY + 80,
      eventLogOn ? '[ EVENT LOG: ON ]' : '[ EVENT LOG: OFF ]',
      Font.sizeButton, ButtonColor.muted, () => {
        eventLogOn = !eventLogOn;
        logBtn.setText(eventLogOn ? '[ EVENT LOG: ON ]' : '[ EVENT LOG: OFF ]');
        const settings = saveSystem.loadSettings();
        settings.eventLogEnabled = eventLogOn;
        saveSystem.saveSettings(settings);
        this.game.events.emit('event-log-toggled', eventLogOn);
      });

    crispButton(this, width / 2, btnY + 120, '[ DEBUG ]',
      Font.sizeButton, ButtonColor.debug, () => this.openDebug());
    crispButton(this, width / 2, btnY + 160, '[ QUIT TO MENU ]',
      Font.sizeButton, ButtonColor.warning, () => this.quitToMenu());

    // Pause key to resume
    if (this.input.keyboard) {
      const pauseBinding = KeyBindings.getBinding(GameAction.Pause);
      this.escKey = this.input.keyboard.addKey(pauseBinding.primary);
      this.escKey.on('down', () => this.resume());
    }
  }

  private resume(): void {
    this.scene.resume('GameScene');
    this.scene.stop();
  }

  private openAttunement(): void {
    const sceneKey = DebugConfig.flags.useTextAttunement
      ? 'AttunementSceneText'
      : 'AttunementTreeScene';
    this.scene.start(sceneKey);
  }

  private openDebug(): void {
    this.scene.start('DebugSettingsScene');
  }

  private quitToMenu(): void {
    this.scene.stop('UIScene');
    this.scene.stop('GameScene');
    this.scene.start('MenuScene');
  }
}
